/**
 * Japan's flagship-resort deep dive: Furano (/furano, /furano/travel-guide,
 * /furano/faq). Rendered by src/pages/flagship/*.tsx; the route slug comes
 * from SiteConfig.routes.flagship. Shape: FlagshipData in src/data/flagship.ts.
 *
 * Deep-dive notes (formerly src/data/furanoDeepDive.ts):
 * Furano content push: opening dates, snowfall patterns, equipment rentals and
 * local travel tips. Rendered inside src/pages/FuranoPage.tsx, and linked from
 * the season calendar and the FAQ.
 *
 * Season dates match src/utils/resortSeasons.ts (Furano: late November to early
 * May). Prices are 2024-2025 season indications in JPY.
 */
import type { FlagshipData } from '@/data/flagship';

export const FLAGSHIP: FlagshipData = {
  resortSlug: 'furano',
  name: { ja: '富良野', en: 'Furano', ko: '후라노' },
  resortName: { ja: '富良野スキー場', en: 'Furano Ski Resort', ko: '후라노 스키 리조트' },
  badge: { ja: '北海道 富良野', en: 'Hokkaido · Furano', ko: '홋카이도 후라노' },
  address: 'Furano, Hokkaido, Japan',
  region: 'Hokkaido',
  latitude: 43.3167,
  longitude: 142.4,
  faqRelated: {
    resortGuide: { ja: '富良野スキー場ガイド', en: 'Furano resort guide', ko: '후라노 스키장 가이드' },
    travelGuide: { ja: '富良野 旅行ガイド', en: 'Furano travel guide', ko: '후라노 여행 가이드' },
    countryFaq: { ja: '日本のスキー全般のFAQ', en: 'Japan skiing FAQ', ko: '일본 스키 전체 FAQ' },
  },
  main: {
    ja: {
      title: '富良野スキー場ガイド｜ライブカメラ・積雪・リフト券',
      description:
        '富良野スキー場のガイド。ライブカメラ、積雪量、コース構成、リフト券料金、旭川空港からのアクセス、ニセコとの違いまで解説します。',
      keywords: '富良野スキー場, 富良野 ライブカメラ, 富良野 積雪, 富良野 リフト券, 富良野 スキー, 北海道 スキー場',
      h1: '富良野スキー場ガイド',
      lede:
        '富良野は北海道中央部・上川盆地の内陸に位置し、日本でもっとも乾いた雪が降るエリアのひとつです。年間降雪量は約9メートル、ニセコより気温が低く湿度が低いため、パウダーが軽く長持ちします。混雑はニセコの半分以下で、旭川空港から約1時間。',
      factsHeading: '基本データ',
      facts: [
        { label: '所在地', value: '北海道 富良野市' },
        { label: '標高差', value: '約839m（頂上1,074m / ベース235m）' },
        { label: 'コース数', value: '28コース（初級40% / 中級40% / 上級20%）' },
        { label: 'リフト', value: 'ロープウェイ2本を含む9基' },
        { label: '年間降雪量', value: '約900cm（乾いた内陸性の雪）' },
        { label: '最寄り空港', value: '旭川空港（車で約60分）' },
      ],
      zonesHeading: '2つのゾーン',
      zones: [
        {
          name: '富良野ゾーン',
          body: 'ロープウェイで一気に標高900m付近まで上がれるメインゾーン。幅の広い中級コースが多く、ワールドカップ開催実績のある斜面もここにあります。',
        },
        {
          name: '北の峰ゾーン',
          body: '宿泊施設が集まるベース。緩斜面とナイター営業があり、初心者とファミリーに向いています。',
        },
      ],
      snowHeading: '雪質と混雑',
      snowBody:
        '1月中旬から2月中旬がベストシーズン。内陸のため日中も-8℃前後を保ち、降った雪がそのまま残ります。海に近いニセコは湿度が高い日があり、富良野の雪はより軽い一方で降雪量そのものはニセコより少なめです。週末は札幌からの日帰り客で午前中の駐車場が混みますが、リフト待ちは平日ほぼゼロです。',
      ticketHeading: 'リフト券の目安',
      ticketBody:
        '2026-2027シーズンの大人1日券はレギュラー期9,000円（SEIBU PRINCE CLUB会員7,500円）、小学生以下は無料です。宿泊パッケージに含まれることも多くあります。最新価格はリゾート詳細ページで確認してください。',
      accessHeading: 'アクセス',
      accessBody:
        '旭川空港からバスまたはレンタカーで約60分、新千歳空港からは約3時間。JRは富良野駅からシャトルバスで約10分。冬季はレンタカーの雪道運転に慣れていない場合、空港連絡バスが確実です。',
      linksHeading: '富良野をさらに詳しく',
      links: [
        { to: '/furano/travel-guide', label: '富良野 旅行ガイド（宿・食事・モデル日程）', icon: 'plane' },
        { to: '/furano/faq', label: '富良野スキー場 よくある質問', icon: 'ticket' },
        { to: '/resort/furano', label: '富良野スキー場 ライブカメラと積雪データ', icon: 'camera' },
        { to: '/season-calendar', label: 'オープン・クローズ時期を確認', icon: 'calendar' },
      ],
      faq: [
        {
          q: '富良野スキー場はいつオープンしますか？',
          a: '例年11月下旬にオープンし、5月上旬までの営業です。北の峰ゾーンから先に開き、富良野ゾーンは12月に全面オープンします。',
        },
        {
          q: '富良野とニセコはどちらがいいですか？',
          a: '降雪量と外国人向けサービスならニセコ、雪の軽さと空いている斜面なら富良野です。富良野はリフト待ちが少なく、旭川空港から近い点も有利です。',
        },
        {
          q: '初心者や子ども連れでも大丈夫ですか？',
          a: '北の峰ゾーンに緩斜面とスクールが揃っており、ファミリー向きです。ナイター営業もあります。',
        },
      ],
    },
    en: {
      title: 'Furano Ski Resort: Webcams, Snow and Lift Tickets',
      description:
        'Furano guide: live cameras, snowfall, terrain, lift ticket prices, access from Asahikawa Airport and how it compares with Niseko.',
      keywords: 'furano ski resort, furano japan skiing, furano snow report, furano lift tickets, furano vs niseko, hokkaido skiing',
      h1: 'Furano Ski Resort Guide',
      lede:
        'Furano sits inland in central Hokkaido, in one of the driest snow zones in Japan. It gets around nine metres of snow a year, and because it is colder and less humid than coastal Niseko, that snow stays light for days. Crowds are a fraction of Niseko\'s, and Asahikawa Airport is an hour away.',
      factsHeading: 'The numbers',
      facts: [
        { label: 'Location', value: 'Furano City, Hokkaido' },
        { label: 'Vertical', value: '839 m (summit 1,074 m / base 235 m)' },
        { label: 'Runs', value: '28 (40% beginner / 40% intermediate / 20% advanced)' },
        { label: 'Lifts', value: '9, including two ropeways' },
        { label: 'Annual snowfall', value: '~900 cm of dry, inland snow' },
        { label: 'Nearest airport', value: 'Asahikawa (about 60 minutes)' },
      ],
      zonesHeading: 'Two zones',
      zones: [
        {
          name: 'Furano Zone',
          body: 'The main mountain, with a ropeway that lifts you to around 900 m in one go. Wide intermediate pistes, plus the World Cup race slope.',
        },
        {
          name: 'Kitanomine Zone',
          body: 'The village side where most lodging sits. Gentle terrain and night skiing make it the family and beginner base.',
        },
      ],
      snowHeading: 'Snow and crowds',
      snowBody:
        'Mid-January to mid-February is the sweet spot. Daytime temperatures hold near -8°C, so fresh snow does not settle or crust. Niseko gets more total snowfall and occasional humid days; Furano gets less volume but lighter, drier powder. Weekend mornings fill the car park with Sapporo day-trippers, but lift queues on weekdays are effectively zero.',
      ticketHeading: 'Lift tickets',
      ticketBody:
        'A single adult day is ¥9,000 in the 2026-2027 regular period (¥7,500 for SEIBU PRINCE CLUB members), and children of elementary age and under ski free. Tickets are often bundled into accommodation packages. Check the resort page for current prices.',
      accessHeading: 'Getting there',
      accessBody:
        'About 60 minutes from Asahikawa Airport by bus or hire car, roughly 3 hours from New Chitose. By train, Furano Station is a 10-minute shuttle ride away. If you are not confident driving on snow, take the airport coach.',
      linksHeading: 'More on Furano',
      links: [
        { to: '/furano/travel-guide', label: 'Furano travel guide: lodging, food, itineraries', icon: 'plane' },
        { to: '/furano/faq', label: 'Furano FAQ: snow, crowds and costs', icon: 'ticket' },
        { to: '/resort/furano', label: 'Furano live webcams and snow data', icon: 'camera' },
        { to: '/season-calendar', label: 'Check opening and closing dates', icon: 'calendar' },
      ],
      faq: [
        {
          q: 'When does Furano open for the ski season?',
          a: 'Furano typically opens in late November and runs to early May. Kitanomine opens first; the Furano Zone is usually fully open by December.',
        },
        {
          q: 'Should I choose Furano or Niseko?',
          a: 'Niseko for sheer snowfall and English-language services; Furano for drier snow, quieter slopes and a shorter airport transfer.',
        },
        {
          q: 'Is Furano good for beginners and families?',
          a: 'Yes. The Kitanomine side has gentle runs, ski schools and night skiing, and lodging is right at the base.',
        },
      ],
    },
    ko: {
      title: '후라노 스키장 가이드: 라이브 카메라·적설·리프트권',
      description:
        '후라노 스키장 가이드: 라이브 카메라, 적설량, 코스 구성, 리프트권 요금, 아사히카와 공항 교통편, 니세코와의 차이.',
      keywords: '후라노 스키장, 후라노 스키, 후라노 적설, 후라노 리프트권, 홋카이도 스키장',
      h1: '후라노 스키장 가이드',
      lede:
        '후라노는 홋카이도 중앙 내륙에 있어 일본에서 가장 건조한 눈이 내리는 지역 중 하나입니다. 연간 강설량은 약 9m이며, 니세코보다 기온이 낮고 습도가 낮아 파우더가 오래 유지됩니다. 아사히카와 공항에서 약 1시간.',
      factsHeading: '기본 정보',
      facts: [
        { label: '위치', value: '홋카이도 후라노시' },
        { label: '표고차', value: '약 839m (정상 1,074m / 베이스 235m)' },
        { label: '코스 수', value: '28개 (초급 40% / 중급 40% / 상급 20%)' },
        { label: '리프트', value: '로프웨이 2기를 포함한 9기' },
        { label: '연간 강설량', value: '약 900cm의 건조한 내륙 설질' },
        { label: '가까운 공항', value: '아사히카와 공항 (약 60분)' },
      ],
      zonesHeading: '두 개의 존',
      zones: [
        { name: '후라노 존', body: '로프웨이로 표고 약 900m까지 한 번에 오르는 메인 존. 넓은 중급 코스와 월드컵 개최 슬로프가 있습니다.' },
        { name: '기타노미네 존', body: '숙소가 모여 있는 베이스. 완사면과 야간 스키가 있어 초급자·가족에게 적합합니다.' },
      ],
      snowHeading: '설질과 혼잡도',
      snowBody:
        '1월 중순~2월 중순이 최적기입니다. 낮에도 -8℃ 전후를 유지해 내린 눈이 그대로 남습니다. 니세코는 강설량이 더 많지만 습한 날이 있고, 후라노는 양은 적지만 더 가볍고 건조합니다. 주말 오전에는 삿포로 당일 방문객이 많지만 평일 리프트 대기는 거의 없습니다.',
      ticketHeading: '리프트권 요금',
      ticketBody:
        '2026-2027 시즌 성인 1일권은 레귤러 기간 9,000엔(SEIBU PRINCE CLUB 회원 7,500엔)이며 초등학생 이하는 무료입니다. 숙박 패키지에 포함되는 경우가 많습니다.',
      accessHeading: '교통편',
      accessBody:
        '아사히카와 공항에서 버스 또는 렌터카로 약 60분, 신치토세 공항에서는 약 3시간. JR 후라노역에서 셔틀버스로 약 10분입니다.',
      linksHeading: '후라노 더 알아보기',
      links: [
        { to: '/furano/travel-guide', label: '후라노 여행 가이드: 숙소·음식·일정', icon: 'plane' },
        { to: '/furano/faq', label: '후라노 스키장 자주 묻는 질문', icon: 'ticket' },
        { to: '/resort/furano', label: '후라노 라이브 카메라·적설 데이터', icon: 'camera' },
        { to: '/season-calendar', label: '개장·폐장 시기 확인', icon: 'calendar' },
      ],
      faq: [
        { q: '후라노 스키장은 언제 개장하나요?', a: '보통 11월 말 개장해 5월 초까지 운영합니다. 기타노미네 존이 먼저 열리고 후라노 존은 12월에 전면 개장합니다.' },
        { q: '후라노와 니세코 중 어디가 좋나요?', a: '강설량과 영어 서비스는 니세코, 건조한 설질과 한적한 슬로프는 후라노입니다.' },
        { q: '초급자와 아이 동반도 괜찮나요?', a: '기타노미네 존에 완사면과 스키 스쿨이 있어 가족 여행에 적합하며 야간 스키도 운영합니다.' },
      ],
    },
  },
  deepDive: {
    ja: {
      heading: '富良野を計画する：日程・雪・レンタル・現地の動き方',
      intro:
        '富良野は「いつ行くか」で体験が大きく変わる山です。オープン日程、降雪の傾向、レンタルの実際、そして現地での移動と食事まで、旅程を組むために必要な部分をまとめました。',
      sections: [
        {
          id: 'dates',
          heading: 'オープンとクローズの日程',
          body:
            '例年11月下旬に北の峰ゾーンから部分オープンし、12月中旬までに富良野ゾーンを含む全面営業になります。クローズは5月上旬で、4月中旬以降は上部のみの営業に切り替わることが多いです。正式な日程は10月頃に公式サイトで発表され、その年の降雪次第で前後します。',
          bullets: [
            '11月下旬：北の峰ゾーンで部分オープン（人工降雪併用）',
            '12月中旬：ロープウェイを含む全面オープン',
            '1月中旬〜2月中旬：積雪と雪質のピーク',
            '3月：晴天が増え、上部は良好、下部は昼に緩む',
            '4月〜5月上旬：上部中心の春スキー、ナイターは終了',
          ],
        },
        {
          id: 'snowfall',
          heading: '降雪の傾向',
          body:
            '年間降雪量は約900cm。海から離れた内陸のため、ニセコのような1日50cmの大量降雪は少ない代わりに、20〜30cmの降雪が高い頻度で続きます。日中も-8℃前後を保つため、降った雪が数日間そのまま軽く残るのが富良野の最大の強みです。1月下旬から2月中旬は連続降雪日が最も多く、朝一のロープウェイで山頂から新雪を狙うのが定番です。3月は日中の日射で下部が緩む一方、標高900m以上の斜面は乾いた雪が残ります。',
        },
        {
          id: 'rentals',
          heading: 'レンタルと装備',
          body:
            'ゲレンデ直結のレンタルは北の峰ゾーンと富良野ゾーンの両ベースにあり、スキー・ボードのセットが1日5,000〜8,000円、ウェア上下が1日4,000〜5,000円が目安です。パウダー用の太い板やファットスキーは台数が限られるため、1月〜2月の週末は事前予約が確実。ブーツは30cm前後まで用意がありますが、大きいサイズは早く出るため到着日の朝に借りるより前日予約が安全です。ヘルメットは義務ではありませんが、ツリーラン周辺では強く推奨されます。子ども用のセットとキャリーはホテルのレンタルカウンターでも扱っています。',
          bullets: [
            'スキー／ボードセット：1日 5,000〜8,000円',
            'ウェア上下：1日 4,000〜5,000円',
            'パウダーボード・ファットスキー：要予約、台数限定',
            'ブーツ：〜30cm程度まで（大きいサイズは前日予約推奨）',
            '子ども用セット：ホテルのカウンターでも貸出可',
          ],
        },
        {
          id: 'localtips',
          heading: '現地の動き方と食事',
          body:
            '富良野の街とスキー場は約5km離れており、冬季は宿泊施設のシャトルバスか路線バスで移動します。レンタカーは自由度が高い反面、圧雪路と夜間の凍結があるため冬道の経験がない場合は避けたほうが無難です。食事は北の峰にゲレンデ前の飲食店が集まり、街側にはスープカレー、ジンギスカン、富良野産のチーズと乳製品を出す店があります。19時以降は予約なしで入れない店もあるため、宿のフロントに頼んで前日に押さえるのが確実です。滑らない日は、ふらのワインハウス、青い池（美瑛、車で約40分）、旭山動物園（約1時間）が定番の選択肢です。',
          bullets: [
            '街とスキー場は約5km、シャトルまたは路線バスで移動',
            'レンタカーは冬道運転の経験がある場合に限る',
            '名物：スープカレー、ジンギスカン、富良野チーズ',
            '滑らない日：ふらのワインハウス、美瑛の青い池、旭山動物園',
            '旭川空港からのバスは便数が限られるため事前確認を',
          ],
        },
      ],
    },
    en: {
      heading: 'Planning Furano: dates, snowfall, rentals and getting around',
      intro:
        'Furano changes a lot depending on when you go. Here are the opening and closing dates, how the snow actually falls, what renting gear costs, and how to move around town once you arrive.',
      sections: [
        {
          id: 'dates',
          heading: 'Opening and closing dates',
          body:
            'Furano usually opens a partial area on the Kitanomine side in late November, with the full mountain including the ropeway running by mid-December. Closing is in early May, and from mid-April operations often shift to the upper mountain only. Official dates are announced around October and shift with early-season snowfall.',
          bullets: [
            'Late November: partial opening at Kitanomine, helped by snowmaking',
            'Mid-December: full mountain including the ropeways',
            'Mid-January to mid-February: peak depth and best snow quality',
            'March: more sunshine, excellent up high, softening down low by afternoon',
            'April to early May: upper-mountain spring skiing, night skiing finished',
          ],
        },
        {
          id: 'snowfall',
          heading: 'How the snow falls here',
          body:
            'Furano averages around 900 cm a season. Being inland, it rarely gets Niseko\'s 50 cm dumps, but it gets frequent 20–30 cm refills, and because daytime temperatures sit near −8°C, that snow stays light for days. Late January to mid-February brings the longest runs of consecutive snow days, and the usual plan is the first ropeway and fresh lines off the summit. In March the sun softens the lower mountain by midday while anything above 900 m stays dry.',
        },
        {
          id: 'rentals',
          heading: 'Equipment rentals',
          body:
            'Both bases, Kitanomine and the Furano zone, have ski-in rental shops. Budget ¥5,000–8,000 a day for a ski or board set and ¥4,000–5,000 for jacket and pants. Powder boards and fat skis exist but in small numbers, so reserve them for January and February weekends. Boots go up to roughly 30 cm, and the large sizes go first: book the evening before instead of turning up at 8:30 am. Helmets are not mandatory but strongly advised around the tree runs. Kids\' sets and sleds are also available at hotel rental desks.',
          bullets: [
            'Ski or board set: ¥5,000–8,000 per day',
            'Jacket and pants: ¥4,000–5,000 per day',
            'Powder boards and fat skis: limited stock, reserve ahead',
            'Boots: up to about 30 cm; book large sizes the day before',
            'Kids\' gear: available at most hotel rental desks',
          ],
        },
        {
          id: 'localtips',
          heading: 'Getting around and eating',
          body:
            'Furano town sits about 5 km from the slopes, connected in winter by hotel shuttles and local buses. A rental car gives freedom but means packed snow and overnight ice, so skip it if you have not driven winter roads. Kitanomine has the slopeside restaurants; town has soup curry, Genghis Khan lamb barbecue and shops built around Furano dairy and cheese. Small places fill after 7 pm, so have your accommodation book the night before. On a non-ski day, Furano Winery, the Blue Pond at Biei (40 minutes) and Asahiyama Zoo (about an hour) are the standard options.',
          bullets: [
            'Town is ~5 km from the slopes: hotel shuttle or local bus',
            'Only drive if you are used to snow-packed roads',
            'Eat: soup curry, Genghis Khan lamb, Furano cheese',
            'Non-ski days: Furano Winery, Blue Pond at Biei, Asahiyama Zoo',
            'Airport buses from Asahikawa are limited, so check times in advance',
          ],
        },
      ],
    },
    ko: {
      heading: '후라노 여행 계획: 일정·강설·렌탈·현지 이동',
      intro:
        '후라노는 언제 가는지에 따라 경험이 크게 달라집니다. 개장·폐장 일정, 실제 강설 패턴, 장비 렌탈 비용, 현지 이동과 식사까지 정리했습니다.',
      sections: [
        {
          id: 'dates',
          heading: '개장과 폐장 일정',
          body:
            '보통 11월 말 기타노미네 구역부터 부분 개장하고, 12월 중순까지 로프웨이를 포함한 전면 운영에 들어갑니다. 폐장은 5월 초이며 4월 중순 이후에는 상부만 운영하는 경우가 많습니다. 공식 일정은 10월경 발표되며 초겨울 적설에 따라 달라집니다.',
          bullets: [
            '11월 말: 기타노미네 구역 부분 개장 (인공 강설 병행)',
            '12월 중순: 로프웨이 포함 전면 개장',
            '1월 중순~2월 중순: 적설과 설질 절정',
            '3월: 맑은 날 증가, 상부는 좋고 하부는 오후에 물러짐',
            '4월~5월 초: 상부 중심 봄 스키, 야간 운영 종료',
          ],
        },
        {
          id: 'snowfall',
          heading: '강설 패턴',
          body:
            '연간 강설량은 약 900cm입니다. 내륙이라 니세코처럼 하루 50cm가 쏟아지는 일은 드물지만 20~30cm 강설이 자주 반복되고, 낮 기온이 영하 8도 안팎이라 내린 눈이 며칠간 가볍게 유지됩니다. 1월 말~2월 중순에 연속 강설일이 가장 많으며, 첫 로프웨이로 정상 신설을 노리는 것이 정석입니다.',
        },
        {
          id: 'rentals',
          heading: '장비 렌탈',
          body:
            '기타노미네와 후라노 존 양쪽 베이스에 렌탈 숍이 있습니다. 스키·보드 세트는 1일 5,000~8,000엔, 상하 웨어는 1일 4,000~5,000엔 정도입니다. 파우더 보드와 팻 스키는 수량이 적어 1~2월 주말에는 사전 예약이 안전합니다. 부츠는 약 30cm까지 있지만 큰 사이즈는 빨리 나가므로 전날 예약을 권합니다. 헬멧은 의무가 아니지만 트리런 구역에서는 강력히 권장됩니다.',
          bullets: [
            '스키·보드 세트: 1일 5,000~8,000엔',
            '상하 웨어: 1일 4,000~5,000엔',
            '파우더 보드·팻 스키: 수량 한정, 사전 예약 권장',
            '부츠: 약 30cm까지 (큰 사이즈는 전날 예약)',
            '아동용 세트: 호텔 렌탈 카운터에서도 대여 가능',
          ],
        },
        {
          id: 'localtips',
          heading: '현지 이동과 식사',
          body:
            '후라노 시내와 스키장은 약 5km 떨어져 있어 겨울에는 숙소 셔틀이나 노선버스로 이동합니다. 렌터카는 자유롭지만 압설로와 야간 결빙이 있어 겨울 운전 경험이 없다면 피하는 편이 안전합니다. 기타노미네에는 슬로프 앞 식당이 모여 있고, 시내에는 수프카레, 징기스칸, 후라노 치즈를 내는 가게가 있습니다. 19시 이후에는 예약 없이 들어가기 어려운 곳도 있어 전날 숙소에 예약을 부탁하는 것이 확실합니다.',
          bullets: [
            '시내와 스키장은 약 5km, 셔틀 또는 노선버스 이용',
            '렌터카는 겨울 도로 경험이 있을 때만 권장',
            '먹거리: 수프카레, 징기스칸, 후라노 치즈',
            '스키를 쉬는 날: 후라노 와인하우스, 비에이 푸른 못, 아사히야마 동물원',
            '아사히카와 공항버스는 편수가 적어 사전 확인 필요',
          ],
        },
      ],
    },
  },
  guide: {
    ja: {
      title: '富良野 旅行ガイド｜アクセス・宿・食事・日程の組み方',
      description:
        '旭川空港・札幌からの行き方、宿の選び方、食事、5日間のモデル日程まで、富良野スキー旅行を組み立てるための実務ガイドです。',
      keywords: '富良野 旅行, 富良野 アクセス, 富良野 宿, 富良野 スキー 日程, 旭川空港 富良野',
      h1: '富良野スキー旅行ガイド',
      lede: '富良野は「空港が近く、雪が軽く、混まない」北海道旅行の組み立てやすい拠点です。移動、宿、食事、日程の順に実務的にまとめます。',
      sections: [
        {
          icon: 'plane',
          heading: 'アクセス：旭川空港が正解',
          body: [
            '旭川空港から車で約60分、冬季は空港連絡バス（ラベンダー号など）が富良野の主要ホテルを回ります。新千歳空港からは約3時間かかるため、可能なら旭川発着の便を選んでください。',
            'JRを使う場合は旭川駅から富良野線で約70分、富良野駅からスキー場までシャトルバスで10分。レンタカーは旭川空港で借りるのが最短ですが、圧雪路と地吹雪に慣れていない場合はバスが安全です。',
          ],
        },
        {
          icon: 'hotel',
          heading: '宿：北の峰か市街地か',
          body: [
            '北の峰ゾーンはゲレンデ直結で、朝一番のロープウェイに歩いて行けます。ホテル、コンドミニアム、ペンションが混在し、リフト券込みのパッケージが多いのが特徴です。',
            '富良野市街は宿代が安く、飲食店の選択肢が広い代わりに毎日シャトルまたは車移動が必要です。3泊以上で外食を楽しむなら市街、滑走時間を最大化するなら北の峰。',
          ],
        },
        {
          icon: 'utensils',
          heading: '食事：カレーとチーズと夜の店',
          body: [
            '富良野は農産地なので食事のレベルが高く、名物は富良野オムカレー。市街地の唯我独尊やくまげらといった老舗は夕食時に混むため予約が安全です。',
            'ゲレンデ側は選択肢が限られ、20時を過ぎると閉まる店が増えます。コンビニは市街に集まっているので、初日に飲み物と朝食を買っておくと楽です。',
          ],
        },
        {
          icon: 'calendar',
          heading: '5日間のモデル日程',
          body: [
            '1日目：旭川空港着、バスで富良野へ。北の峰でレンタルを済ませ、ナイターで足慣らし。',
            '2日目：富良野ゾーンのロープウェイで朝一。午前中に上部の非圧雪、午後は中級コースを流す。',
            '3日目：早朝に降雪があればツリーラン系のコースへ。午後は市街地で温泉と食事。',
            '4日目：足を休めるならトマムまたはカムイスキーリンクスに車で日帰り（各1〜1.5時間）。',
            '5日目：午前だけ滑り、シャトルで旭川空港へ。冬季は道路状況の遅れを見て3時間前に出発。',
          ],
        },
      ],
      linksHeading: '関連ページ',
      links: [
        { to: '/furano', label: '富良野スキー場ガイド（雪質・コース・リフト券）' },
        { to: '/furano/faq', label: '富良野スキー場 よくある質問' },
        { to: '/resort/furano', label: '富良野のライブカメラと積雪データ' },
        { to: '/season-calendar', label: '日本のスキーシーズンカレンダー' },
      ],
    },
    en: {
      title: 'Furano Travel Guide: Access, Lodging, Food, Itineraries',
      description:
        'How to reach Furano from Asahikawa or Sapporo, where to stay, what to eat, and a practical five-day Furano ski itinerary.',
      keywords: 'furano travel guide, furano access, asahikawa airport to furano, where to stay furano, furano ski itinerary',
      h1: 'Furano Ski Trip Guide',
      lede: 'Furano is one of the easiest Hokkaido trips to plan: a close airport, dry snow and no queues. This page covers getting there, where to sleep, what to eat and how to plan the days.',
      sections: [
        {
          icon: 'plane',
          heading: 'Access: fly into Asahikawa',
          body: [
            'Asahikawa Airport is about 60 minutes by road, and winter coaches (the Lavender liner and similar) call at the main Furano hotels. New Chitose is roughly three hours away, so book Asahikawa if the fare allows.',
            'By train it is about 70 minutes from Asahikawa Station on the Furano Line, then a 10-minute shuttle to the slopes. Hire cars are quickest, but if packed snow and ground blizzards are new to you, take the bus.',
          ],
        },
        {
          icon: 'hotel',
          heading: 'Lodging: Kitanomine or town',
          body: [
            'Kitanomine is ski-in, ski-out: you can walk to the first ropeway. It mixes hotels, condos and pensions, and most sell lift-ticket packages.',
            'Furano town is cheaper with far more restaurants, but you shuttle or drive daily. Three nights or more with dinners out favours town; maximising runs favours Kitanomine.',
          ],
        },
        {
          icon: 'utensils',
          heading: 'Food: curry, dairy and early closing',
          body: [
            'This is farming country, and the local dish is Furano omu-curry. Long-running places in town such as Yuigadokuson and Kumagera fill up at dinner, so book.',
            'Slope-side options are thin and many kitchens shut after 8pm. Convenience stores cluster in town, so buy breakfast and drinks on arrival day.',
          ],
        },
        {
          icon: 'calendar',
          heading: 'A five-day plan',
          body: [
            'Day 1: land at Asahikawa, coach to Furano, sort rentals at Kitanomine and warm up under the night lights.',
            'Day 2: first ropeway in the Furano Zone. Ungroomed upper terrain in the morning, cruising intermediates after lunch.',
            'Day 3: if it snowed overnight, head for the treed lines. Onsen and a proper dinner in town afterwards.',
            'Day 4: rest the legs with a day trip to Tomamu or Kamui Ski Links (1–1.5 hours each by car).',
            'Day 5: ski the morning, then shuttle to Asahikawa. In winter, leave three hours before the flight because the roads are slow.',
          ],
        },
      ],
      linksHeading: 'Related pages',
      links: [
        { to: '/furano', label: 'Furano ski resort guide: snow, terrain, lift tickets' },
        { to: '/furano/faq', label: 'Furano FAQ' },
        { to: '/resort/furano', label: 'Furano live webcams and snow data' },
        { to: '/season-calendar', label: 'Japan ski season calendar' },
      ],
    },
    ko: {
      title: '후라노 여행 가이드: 교통·숙소·음식·일정',
      description: '아사히카와·삿포로에서 후라노 가는 방법, 숙소 선택, 음식, 5일 스키 일정을 정리한 실용 가이드입니다.',
      keywords: '후라노 여행, 후라노 교통, 아사히카와 공항 후라노, 후라노 숙소, 후라노 스키 일정',
      h1: '후라노 스키 여행 가이드',
      lede: '후라노는 공항이 가깝고 눈이 건조하며 붐비지 않아 홋카이도 여행을 계획하기 쉬운 곳입니다. 교통, 숙소, 음식, 일정 순으로 정리했습니다.',
      sections: [
        {
          icon: 'plane',
          heading: '교통: 아사히카와 공항이 정답',
          body: [
            '아사히카와 공항에서 차로 약 60분이며 겨울에는 공항 연결버스가 주요 호텔을 순회합니다. 신치토세 공항에서는 약 3시간이 걸립니다.',
            'JR는 아사히카와역에서 후라노선으로 약 70분, 후라노역에서 셔틀버스로 10분입니다. 설로 운전에 익숙하지 않다면 버스가 안전합니다.',
          ],
        },
        {
          icon: 'hotel',
          heading: '숙소: 기타노미네 또는 시내',
          body: [
            '기타노미네는 슬로프와 바로 연결되어 첫 로프웨이까지 걸어갈 수 있고, 리프트권 포함 패키지가 많습니다.',
            '후라노 시내는 숙박비가 저렴하고 음식점이 많지만 매일 셔틀이나 차량 이동이 필요합니다.',
          ],
        },
        {
          icon: 'utensils',
          heading: '음식: 카레와 유제품',
          body: [
            '농업 지역이라 음식 수준이 높고 명물은 후라노 오므카레입니다. 시내 노포는 저녁에 붐비니 예약을 권합니다.',
            '슬로프 주변은 선택지가 적고 20시 이후 문을 닫는 곳이 많습니다. 편의점은 시내에 모여 있습니다.',
          ],
        },
        {
          icon: 'calendar',
          heading: '5일 일정 예시',
          body: [
            '1일차: 아사히카와 공항 도착, 버스로 후라노 이동, 기타노미네에서 장비 대여 후 야간 스키.',
            '2일차: 후라노 존 첫 로프웨이. 오전 상부 비압설, 오후 중급 코스.',
            '3일차: 밤사이 눈이 왔다면 트리런 코스. 오후에는 시내 온천과 식사.',
            '4일차: 토마무 또는 카무이 스키링크스 당일치기(차로 1~1.5시간).',
            '5일차: 오전만 활강 후 셔틀로 아사히카와 공항. 겨울에는 3시간 전 출발.',
          ],
        },
      ],
      linksHeading: '관련 페이지',
      links: [
        { to: '/furano', label: '후라노 스키장 가이드: 설질·코스·리프트권' },
        { to: '/furano/faq', label: '후라노 자주 묻는 질문' },
        { to: '/resort/furano', label: '후라노 라이브 카메라·적설 데이터' },
        { to: '/season-calendar', label: '일본 스키 시즌 캘린더' },
      ],
    },
  },
  faq: {
    ja: {
      title: '富良野スキー場 よくある質問｜雪質・混雑・料金',
      description:
        '富良野の雪質、混雑状況、リフト券料金、ニセコとの違い、子連れ向きか、レンタルや英語対応など、よくある疑問に答えます。',
      keywords: '富良野 よくある質問, 富良野 雪質, 富良野 混雑, 富良野 リフト券, 富良野 ニセコ 比較',
      h1: '富良野スキー場 よくある質問',
      lede: '富良野を検討している人から実際によく聞かれる質問をまとめました。',
      faq: [
        { q: '富良野の雪質はどのくらい良いのですか？', a: '内陸性の気候で湿度が低く、日中も-8℃前後を保つため、日本でもっとも乾いた雪のひとつです。降雪量は年間約900cmでニセコより少ないものの、雪の軽さは同等以上です。' },
        { q: '富良野は混みますか？', a: '平日のリフト待ちはほぼありません。週末の午前は札幌からの日帰り客で駐車場とロープウェイが混みますが、ニセコの混雑とは比較になりません。' },
        { q: 'リフト券はいくらですか？', a: '2026-2027シーズンの大人1日券はレギュラー期9,000円（SEIBU PRINCE CLUB会員7,500円）、小学生以下は無料です。宿泊パッケージもお得です。' },
        { q: '富良野とニセコ、どちらを選ぶべきですか？', a: '降雪量、ナイトライフ、英語対応を優先するならニセコ。雪の軽さ、空いた斜面、空港からの近さを優先するなら富良野です。予算も富良野の方が抑えられます。' },
        { q: '子ども連れでも楽しめますか？', a: '北の峰ゾーンに緩斜面、キッズパーク、スクールが揃っています。ゲレンデ直結の宿が多く、ナイター営業もあります。' },
        { q: '英語は通じますか？', a: 'メインのチケット窓口とレンタル、主要ホテルでは英語が通じます。市街地の飲食店では通じないこともありますが、写真付きメニューが一般的です。' },
        { q: 'レンタルはできますか？', a: '北の峰ベースとロープウェイ乗り場でスキー、スノーボード、ウェアを一式レンタルできます。ハイシーズンの週末は事前予約が安全です。' },
        { q: 'バックカントリーはできますか？', a: 'ゲート管理された非圧雪エリアがあり、コース外は基本的に禁止です。十勝岳周辺のバックカントリーはガイド同行が前提です。' },
        { q: '滑らない日は何をすればいいですか？', a: '富良野の温泉、チーズ工房、ワイナリー、冬の「富良野スキー場ナイター」以外にも、旭川の旭山動物園まで車で約1時間です。' },
        { q: 'いつ行くのがベストですか？', a: '1月中旬から2月中旬が雪質のピークです。3月は日照が増えて快適ですが、上部以外は雪が緩みます。' },
      ],
    },
    en: {
      title: 'Furano FAQ: Snow Quality, Crowds and Costs',
      description:
        'Furano answered: snow quality, crowd levels, lift ticket prices, how it compares with Niseko, families, rentals and English support.',
      keywords: 'furano faq, furano snow quality, is furano crowded, furano lift ticket price, furano vs niseko',
      h1: 'Furano Ski Resort FAQ',
      lede: 'The questions people actually ask before booking Furano.',
      faq: [
        { q: 'How good is the snow at Furano?', a: 'Among the driest in Japan. Inland air keeps humidity low and daytime temperatures near -8°C, so powder stays light. Total snowfall (~900 cm a year) is lower than Niseko, but the quality is equal or better.' },
        { q: 'Is Furano crowded?', a: 'Weekday lift queues are effectively nonexistent. Weekend mornings bring Sapporo day-trippers to the car park and ropeway, but it never approaches Niseko levels.' },
        { q: 'How much are lift tickets?', a: 'For 2026-2027 an adult day ticket is ¥9,000 in the regular period (¥7,500 for SEIBU PRINCE CLUB members), and children of elementary age and under ski free. Accommodation packages can also save money.' },
        { q: 'Furano or Niseko?', a: 'Niseko for snowfall volume, nightlife and English-language services. Furano for drier snow, empty slopes, a closer airport and lower prices.' },
        { q: 'Is Furano good with children?', a: 'Yes. The Kitanomine side has gentle slopes, a kids park and ski schools, with ski-in lodging and night skiing.' },
        { q: 'Is English spoken?', a: 'At the main ticket windows, rental shops and larger hotels, yes. Some town restaurants are Japanese-only, though picture menus are common.' },
        { q: 'Can I rent gear there?', a: 'Skis, boards and clothing are available at the Kitanomine base and the ropeway building. Reserve ahead for peak-season weekends.' },
        { q: 'What about backcountry?', a: 'There are gate-controlled ungroomed areas; going outside the boundary is otherwise prohibited. Tokachidake backcountry should be done with a guide.' },
        { q: 'What is there to do on a non-ski day?', a: 'Onsen, a cheese factory and a winery in Furano itself, plus Asahiyama Zoo in Asahikawa about an hour away by car.' },
        { q: 'When is the best time to go?', a: 'Mid-January to mid-February for snow quality. March is sunnier and pleasant, but only upper runs hold firm snow.' },
      ],
    },
    ko: {
      title: '후라노 스키장 자주 묻는 질문: 설질·혼잡도·요금',
      description: '후라노의 설질, 혼잡도, 리프트권 요금, 니세코와의 차이, 가족 여행, 렌탈과 영어 대응에 대한 질문에 답합니다.',
      keywords: '후라노 자주 묻는 질문, 후라노 설질, 후라노 혼잡, 후라노 리프트권, 후라노 니세코 비교',
      h1: '후라노 스키장 자주 묻는 질문',
      lede: '후라노를 예약하기 전에 가장 많이 받는 질문들을 정리했습니다.',
      faq: [
        { q: '후라노의 설질은 어떤가요?', a: '내륙 기후로 습도가 낮고 낮에도 -8℃ 전후를 유지해 일본에서 가장 건조한 눈에 속합니다. 강설량은 연 약 900cm로 니세코보다 적지만 설질은 동등 이상입니다.' },
        { q: '후라노는 붐비나요?', a: '평일 리프트 대기는 거의 없습니다. 주말 오전에는 삿포로 당일 방문객이 있지만 니세코와 비교할 수준은 아닙니다.' },
        { q: '리프트권은 얼마인가요?', a: '2026-2027 시즌 성인 1일권은 레귤러 기간 9,000엔(SEIBU PRINCE CLUB 회원 7,500엔)이며 초등학생 이하는 무료입니다. 숙박 패키지도 저렴합니다.' },
        { q: '후라노와 니세코 중 어디를 선택할까요?', a: '강설량·나이트라이프·영어 서비스는 니세코, 건조한 설질·한적한 슬로프·가까운 공항·저렴한 비용은 후라노입니다.' },
        { q: '아이와 함께 가도 좋나요?', a: '기타노미네 존에 완사면, 키즈파크, 스키 스쿨이 있고 슬로프 연결 숙소와 야간 스키도 있습니다.' },
        { q: '영어가 통하나요?', a: '주요 티켓 창구, 렌탈숍, 큰 호텔에서는 통합니다. 시내 일부 식당은 일본어만 가능하지만 사진 메뉴가 흔합니다.' },
        { q: '장비 대여가 가능한가요?', a: '기타노미네 베이스와 로프웨이 건물에서 스키·보드·의류를 대여할 수 있습니다. 성수기 주말은 예약을 권합니다.' },
        { q: '백컨트리는 가능한가요?', a: '게이트로 관리되는 비압설 구역이 있으며 코스 밖은 원칙적으로 금지입니다. 도카치다케 백컨트리는 가이드 동행이 전제입니다.' },
        { q: '스키를 타지 않는 날에는 무엇을 하나요?', a: '후라노의 온천, 치즈 공방, 와이너리가 있고 차로 약 1시간 거리에 아사히야마 동물원이 있습니다.' },
        { q: '가장 좋은 시기는 언제인가요?', a: '설질은 1월 중순~2월 중순이 정점입니다. 3월은 날이 좋지만 상부 외에는 눈이 무릅니다.' },
      ],
    },
  },
};
